import { useEffect, useState } from 'react';

/**
 * Banner discreto que aparece SOLO si:
 *   - El navegador soporta PWA install (Chrome/Edge en Android, Chrome en desktop)
 *   - La app NO está ya instalada como PWA
 *   - El usuario no ha rechazado el prompt en esta sesión
 *
 * En tablets (uso real Elixir) ayuda al vendedor a instalar la app como
 * pantalla completa nativa, sin barras de navegador.
 */
export default function PwaInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // El navegador dispara este evento si la PWA es instalable
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Si ya está instalada (standalone), no mostrar el prompt
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;
    if (isStandalone) setDismissed(true);

    // Recordar dismissed en sessionStorage (no localStorage — no queremos suprimir
    // permanentemente; solo durante la sesión actual del vendedor)
    const sessionDismissed = sessionStorage.getItem('pwa_install_dismissed');
    if (sessionDismissed === '1') setDismissed(true);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!deferredPrompt || dismissed) return null;

  const onInstall = async () => {
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setDismissed(true);
      } else {
        setDismissed(true);
        sessionStorage.setItem('pwa_install_dismissed', '1');
      }
    } catch {
      setDismissed(true);
    }
  };

  const onDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa_install_dismissed', '1');
  };

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 max-w-md w-[calc(100%-3rem)] animate-fade-up">
      <div className="bg-elixir-black/95 border border-elixir-gold/40 rounded-xl2 shadow-card-hover px-5 py-4 flex items-center gap-4 backdrop-blur">
        <div className="flex-1">
          <p className="text-elixir-cream text-sm font-medium">
            Instala Elixir Olfativo
          </p>
          <p className="text-xs text-elixir-cream/60 mt-0.5">
            Acceso directo desde el escritorio, pantalla completa.
          </p>
        </div>
        <button
          onClick={onDismiss}
          className="text-elixir-cream/45 hover:text-elixir-cream text-xs px-3 py-2 transition"
          aria-label="Descartar"
        >
          Ahora no
        </button>
        <button
          onClick={onInstall}
          className="bg-elixir-gold text-elixir-black text-xs font-semibold uppercase tracking-wide px-4 py-2 rounded-md hover:bg-elixir-gold-soft transition"
        >
          Instalar
        </button>
      </div>
    </div>
  );
}
